'use client';

import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  ColumnDef,
  ExpandedState,
} from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { DataNode, FilterState } from '@/types';
import { DecimalNumber } from './DecimalNumber';
import { NodeIcon } from './NodeIcon';
import { RowExpander } from './RowExpander';
import { CumulativeVariancesHeader } from './CumulativeVariancesHeader';
import { EmptyState } from './EmptyState';
import { LoadingMoreState } from './LoadingMoreState';
import { LoadMoreButton } from './LoadMoreButton';
import { TableHeaderRow } from './TableHeaderRow';
import { TableBodyRow } from './TableBodyRow';
import {
  INITIAL_VISIBLE_ROWS,
  LOAD_MORE_INCREMENT,
  EXPANSION_DELAY,
  LOAD_MORE_DELAY,
} from './ReconciliationTable.constants';
import {
  getCellStyle,
  getRowStyle,
  getCumulativeTooltipText,
  filterDataByNodeTag,
} from './ReconciliationTable.utils';
import { createTableColumns } from './ReconciliationTable.columns';

interface ReconciliationTableProps {
  data: DataNode[];
  filterState: FilterState;
  viewTransaction: (row: DataNode) => void;
  highlightedRowId?: string | null;
  cumulativeLoading?: boolean;
  cumulativeFromDate?: string | null;
  businessDay?: string;
  calculatedCumilativeFromDate?: string | null;
}

export function ReconciliationTable({
  data,
  calculatedCumilativeFromDate,
  filterState,
  viewTransaction = () => { },
  highlightedRowId,
  cumulativeLoading,
  cumulativeFromDate,
  businessDay,
}: ReconciliationTableProps) {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [isExpanding, setIsExpanding] = useState(false);
  const expandTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [visibleRows, setVisibleRows] = useState<number>(INITIAL_VISIBLE_ROWS);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const tableRef = useRef<HTMLDivElement>(null);

  // Debounced expansion handler to prevent rapid clicks
  const handleRowExpansion = useCallback(
    (rowId: string, hasChildren: boolean) => {
      if (!hasChildren || isExpanding) return;

      // Clear any existing timeout
      if (expandTimeoutRef.current) {
        clearTimeout(expandTimeoutRef.current);
      }

      setIsExpanding(true);

      // Use requestAnimationFrame to ensure smooth UI updates
      requestAnimationFrame(() => {
        setExpanded((prev) => ({
          ...(prev as Record<string, boolean>),
          [rowId]: !(prev as Record<string, boolean>)[rowId],
        }));

        // Reset expanding state after a short delay
        expandTimeoutRef.current = setTimeout(() => {
          setIsExpanding(false);
        }, EXPANSION_DELAY);
      });
    },
    [isExpanding]
  );

  // Handle loading more rows
  const handleLoadMore = useCallback(() => {
    setIsLoadingMore(true);

    // Simulate a small delay for better UX
    setTimeout(() => {
      setVisibleRows((prev) => prev + LOAD_MORE_INCREMENT);
      setIsLoadingMore(false);
    }, LOAD_MORE_DELAY);
  }, []);

  // Reset visible rows when data changes
  React.useEffect(() => {
    setVisibleRows(INITIAL_VISIBLE_ROWS);
    setIsLoadingMore(false);
  }, [data, filterState]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (expandTimeoutRef.current) {
        clearTimeout(expandTimeoutRef.current);
      }
    };
  }, []);

  const columns = useMemo<ColumnDef<DataNode>[]>(
    () =>
      createTableColumns({
        expanded,
        isExpanding,
        handleRowExpansion,
        viewTransaction,
        calculatedCumilativeFromDate,
        businessDay,
        cumulativeFromDate,
        getCumulativeTooltipText,
      }),
    [
      expanded,
      isExpanding,
      handleRowExpansion,
      viewTransaction,
      calculatedCumilativeFromDate,
      businessDay,
      cumulativeFromDate,
    ]
  );

  const filteredData = useMemo(() => {
    let filtered = data;

    // Apply filters
    Object.entries(filterState).forEach(([filterTag, filterValues]) => {
      if (filterValues.length > 0) {
        filtered = filterDataByNodeTag(filtered, filterTag, filterValues);
      }
    });

    return filtered;
  }, [data, filterState]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getRowId: (row) => row.Id, // Use the Id field from DataNode
    getRowCanExpand: (row) => {
      const hasChildren = Boolean(
        row.original.ChildNodes && row.original.ChildNodes.length > 0
      );
      return hasChildren;
    },
    getSubRows: (row) => row.ChildNodes,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const allRows = table.getExpandedRowModel().rows;
  const displayedRows = allRows.slice(0, visibleRows);
  const hasMoreRows = visibleRows < allRows.length;

  // Function to check if a row should be highlighted
  const isRowHighlighted = (rowId: string): boolean => {
    return highlightedRowId === rowId;
  };

  return (
    <div className="overflow-x-auto bg-white dark:bg-gray-700" ref={tableRef}>
      <table
        className="w-full border-collapse"
        style={{
          // width: table.getCenterTotalSize(),
        }}
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderRow key={headerGroup.id} headerGroup={headerGroup} />
          ))}
        </thead>
        <tbody>
          {displayedRows.map((row, index) => (
            <TableBodyRow
              key={row.id}
              row={row}
              index={index}
              isHighlighted={isRowHighlighted(row.id)}
              cumulativeLoading={cumulativeLoading}
            />
          ))}
          {isLoadingMore && <LoadingMoreState />}
          {allRows.length === 0 && <EmptyState />}
        </tbody>
      </table>
      {hasMoreRows && (
        <div className="p-6 bg-gradient-to-r from-slate-50/50 to-gray-50/30 border-t border-modern-light">
          <div className="flex justify-center">
            <LoadMoreButton
              isLoading={isLoadingMore}
              remainingCount={allRows.length - visibleRows}
              onClick={handleLoadMore}
            />
          </div>
        </div>
      )}
    </div>
  );
}
